import { useState } from "react";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../config/firebase";

const FEATURES = [
  "Runway & burn rate tracking",
  "Departments & headcount",
  "Categories & spending groups",
  "Team members & roles",
  "CSV import",
  "Investor share link",
  "Priority support",
];

const MONTHLY = 50;
const YEARLY  = 500;

export default function StartTrial() {
  const navigate = useNavigate();
  const [interval, setInterval] = useState("month");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handleStart = async () => {
    setError("");
    setLoading(true);
    try {
      const fns = getFunctions(getApp(), "us-central1");
      const createCheckout = httpsCallable(fns, "createCheckoutSession");
      const result = await createCheckout({ interval });
      if (result.data?.url) {
        window.location.href = result.data.url;
      } else {
        throw new Error("No URL returned");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong.");
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const price = interval === "year" ? Math.round(YEARLY / 12) : MONTHLY;

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    }}>
      <div style={{ width: "100%", maxWidth: 400 }}>

        {/* Logo + sign out */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 40 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)" }} />
            <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--text)" }}>greenline</span>
          </div>
          <button
            onClick={handleSignOut}
            style={{ background: "none", border: "none", fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", cursor: "pointer" }}
          >
            sign out
          </button>
        </div>

        {/* Header */}
        <div className="label" style={{ marginBottom: 8 }}>START_FREE_TRIAL</div>
        <div style={{ fontSize: 17, fontWeight: 500, color: "var(--text)", marginBottom: 4 }}>
          14 days free, then pay.
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 28, lineHeight: 1.6 }}>
          enter your card to activate. no charge until your trial ends.
        </div>

        <div className="card hover-green" style={{ borderColor: "var(--green-dim)" }}>

          {/* Price + toggle */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <div className="label" style={{ marginBottom: 4 }}>GREENLINE_PRO</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                <span className="mono" style={{ fontSize: 22, color: "var(--text)", fontWeight: 500 }}>${price}</span>
                <span className="mono" style={{ fontSize: 11, color: "#666" }}>/ month</span>
              </div>
              {interval === "year" && (
                <div className="mono" style={{ fontSize: 10, color: "var(--green)", marginTop: 2 }}>
                  ${YEARLY}/yr — save ${MONTHLY * 12 - YEARLY}
                </div>
              )}
            </div>

            <div style={{ display: "flex", background: "#050505", border: "1px solid #111", borderRadius: 6, padding: 3, gap: 3 }}>
              {["month", "year"].map(i => (
                <button key={i} onClick={() => setInterval(i)} style={{
                  background: interval === i ? "var(--green)" : "transparent",
                  color: interval === i ? "#000" : "#333",
                  border: "none", borderRadius: 4, padding: "4px 10px",
                  cursor: "pointer", fontSize: 10, fontFamily: "var(--mono)",
                  fontWeight: interval === i ? 600 : 400, transition: "all 0.15s",
                }}>
                  {i}ly{i === "year" && (
                    <span style={{ marginLeft: 4, color: interval === "year" ? "#000" : "var(--green)", fontSize: 9 }}>-17%</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Features */}
          <div style={{
            display: "flex", flexDirection: "column", gap: 6,
            marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid var(--border)",
          }}>
            {FEATURES.map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "var(--green)", fontSize: 10 }}>✓</span>
                <span className="mono" style={{ fontSize: 10, color: "#555" }}>{f.toLowerCase()}</span>
              </div>
            ))}
          </div>

          {/* Trial callout */}
          <div style={{
            background: "#050505", border: "1px solid #111", borderRadius: 6,
            padding: "10px 12px", marginBottom: 14,
          }}>
            <div className="mono" style={{ fontSize: 10, color: "var(--green)", marginBottom: 3 }}>
              14-day free trial
            </div>
            <div className="mono" style={{ fontSize: 10, color: "#555", lineHeight: 1.6 }}>
              your card is saved today but not charged.
              after 14 days, you're automatically billed{" "}
              {interval === "year" ? `$${YEARLY}/year` : `$${MONTHLY}/month`}.
              cancel anytime before then.
            </div>
          </div>

          {error && (
            <div className="badge badge-red" style={{ display: "block", marginBottom: 10 }}>{error}</div>
          )}

          <button
            className="btn btn-green"
            onClick={handleStart}
            disabled={loading}
            style={{ width: "100%", fontSize: 12 }}
          >
            {loading ? "redirecting to stripe..." : "start free trial — enter card →"}
          </button>

          <div className="mono" style={{ fontSize: 9, color: "#444", textAlign: "center", marginTop: 8 }}>
            secure via stripe · cancel anytime · no charge for 14 days
          </div>
        </div>
      </div>
    </div>
  );
}
